import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSearch, FaQuestionCircle, FaSpinner } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_BASE_URL } from "../../Apicongfig";

function Search({ onSearch }) {
  const { t } = useTranslation("global");
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchMode, setSearchMode] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const navigate = useNavigate();
  const searchContainerRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);
  const questionIconRef = useRef(null);

  // Question detection patterns
  const questionPatterns = [
    /^(what|who|when|where|why|how|is|are|can|do|does|did|will|would|should|could)\s+/i,
    /^(tell me about|explain|show me|find me|search for|look up)\s+/i,
    /^(quel|quelle|quels|quelles|qui|quand|où|pourquoi|comment)\s+/i,
    /^(dis-moi|explique|montre|trouve|cherche)\s+/i,
  ];

  const isQuestion = (text) => {
    if (text.includes("?")) return true;
    if (text.length > 20 && text.split(" ").length > 4) return true;
    return questionPatterns.some((pattern) => pattern.test(text));
  };

  const userId = parseInt(sessionStorage.getItem("user_id"));
  const fetchSuggestions = useCallback(async (term) => {
    if (term.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/search/${term}?user_id=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      setSuggestions(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);
  // Clear search when route changes
  useEffect(() => {
    setSearchTerm("");
    onSearch("");
    setSearchMode("filter");
  }, [location.pathname]);

  useEffect(() => {
    // Skip if search term is empty (handled in handleSearchChange)
    if (searchTerm.length === 0) return;

    if (isQuestion(searchTerm)) {
      setSearchMode("ai");
      setSuggestions([]);
      return;
    }

    if (searchTerm.length <= 3) {
      setSearchMode("filter");
      onSearch(searchTerm);
      setSuggestions([]);
      return;
    }

    // Default to global search if no suggestions and not in filter/AI mode
    if (suggestions.length === 0 && searchTerm.length > 3) {
      setSearchMode("global");
    } else {
      setSearchMode("filter");
    }

    // setSearchMode("filter");
    fetchSuggestions(searchTerm);
  }, [searchTerm, fetchSuggestions, onSearch]);

  const getSearchIcon = () => {
    switch (searchMode) {
      case "ai":
        return "💬";
      case "global":
        return "📁";
      case "filter":
        return "🔍";
      default:
        return <FaSearch />;
    }
  };

  const getPlaceholder = () => {
    switch (searchMode) {
      case "ai":
        return t("search.defaultPlaceholder");
      case "global":
        return t("search.defaultPlaceholder");
      case "filter":
        return t("search.defaultPlaceholder");
      default:
        return t("search.defaultPlaceholder");
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Immediately reset everything if the field becomes empty
    if (value === "") {
      setSearchMode(null);
      setSuggestions([]);
      onSearch(""); // This will clear any active filters in the parent component
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (searchMode === "ai") {
        navigate(`/question?question=${encodeURIComponent(searchTerm)}`);
      } else if (searchMode === "filter") {
        navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      }
      setSearchTerm("");
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(suggestion.slug);
    setSearchTerm("");
    setSuggestions([]);
  };

  const handleQuestionIconMouseEnter = () => {
    clearTimeout(tooltipTimeoutRef.current);
    setShowTooltip(true);
  };

  const handleQuestionIconMouseLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(tooltipTimeoutRef.current);
    };
  }, []);

  return (
    <div
      ref={searchContainerRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "800px",
        margin: "0 auto",
        display: "flex",
        gap: "10px",
        alignItems: "center",
      }}
    >
      <img
        src="/Assets/landing/logo.png"
        alt="tektime-logo"
        class="img-fluid"
        width={"150"}
      />
      <div
        style={{
          position: "relative",
          width: "100%",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: "20px",
              fontSize: "18px",
              color: "#666",
              zIndex: 1,
            }}
          >
            {getSearchIcon()}
          </span>

          <input
            type="search"
            style={{
              width: "100%",
              padding: "14px 20px 14px 50px",
              fontSize: "16px",
              border: "1px solid #ddd",
              borderRadius: "30px",
              outline: "none",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              transition: "all 0.3s ease",
              backgroundColor: "#fff",
            }}
            placeholder={getPlaceholder()}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setSuggestions([])}
          />

          <button
            ref={questionIconRef}
            onMouseEnter={handleQuestionIconMouseEnter}
            onMouseLeave={handleQuestionIconMouseLeave}
            onClick={() => {
              if (searchMode === "ai") {
                navigate(
                  `/question?question=${encodeURIComponent(searchTerm)}`
                );
              } else if (searchMode === "filter") {
                navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
              }
              setSearchTerm("");
            }}
            style={{
              position: "absolute",
              right: "20px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "18px",
              color: "#666",
              zIndex: 1,
            }}
          >
            <FaQuestionCircle />
          </button>
        </div>

        {/* Tooltip that appears only when hovering the question icon */}
        {showTooltip && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: "20px",
              width: "300px",
              backgroundColor: "#333",
              color: "#fff",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              marginTop: "10px",
              zIndex: 1001,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
          >
            <p style={{ margin: "0 0 8px 0" }}>
              {t("searchTip.tooltip.title")}
            </p>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {t("searchTip.tooltip.options", { returnObjects: true }).map(
                (option, index) => (
                  <li key={index} style={{ marginBottom: "4px" }}>
                    {option}
                  </li>
                )
              )}
            </ul>
            <p style={{ margin: "8px 0 0 0", fontStyle: "italic" }}>
              {t("searchTip.tooltip.footer")}
            </p>
          </div>
        )}

        {searchMode === "filter" && (suggestions.length > 0 || isLoading) && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "#fff",
              border: "1px solid #eee",
              borderRadius: "10px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              marginTop: "10px",
              maxHeight: "400px",
              overflowY: "auto",
              zIndex: 1000,
            }}
          >
            {isLoading ? (
              <div
                style={{
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    animation: "spin 1s linear infinite",
                    fontSize: "24px",
                    color: "#4285f4",
                  }}
                >
                  <FaSpinner />
                </div>
                <div
                  style={{
                    color: "#555",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Searching for results...
                </div>
                <div
                  style={{
                    width: "80%",
                    height: "4px",
                    backgroundColor: "#f1f1f1",
                    borderRadius: "2px",
                    overflow: "hidden",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#4285f4",
                      animation: "loadingBar 1.5s ease-in-out infinite",
                      borderRadius: "2px",
                    }}
                  />
                </div>
              </div>
            ) : (
              suggestions.map((item) => (
                <div
                  key={`${item.type}-${item.slug}`}
                  style={{
                    padding: "12px 15px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => handleSuggestionClick(item)}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f9f9f9")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff")
                  }
                >
                  <span
                    style={{
                      marginRight: "12px",
                      fontSize: "20px",
                      color: item.type === "moment" ? "#4CAF50" : "#2196F3",
                    }}
                  >
                    {item.type === "moment" ?  <img
                                        src="/Assets/sidebar_meeting_active.svg"
                                        alt="meeting"
                                      /> : item?.type === "mission" ?  <img
                                                                              src="/Assets/sidebar_active_destination.svg"
                                                                              alt="mission"
                                                                            />: item?.type === "team" ?   <img
                                        src="/Assets/sidebar_team_active.svg"
                                        alt="team"
                                      /> : item?.type === "step" ? <img
                                        src="/Assets/sidebar-action-active.svg"
                                        alt="action"
                                      /> : item?.type === "solution" ?  <img
                                                                              src="/Assets/Tek.png"
                                                                              alt="solution"
                                                                              width="32px"
                                                                            /> : "👤"}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      {item.title}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#999",
                      textTransform: "capitalize",
                    }}
                  >
                    {t(`entities.${item.type}`)}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;
